import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { localeFromLng } from "@/v2/lib/formatDate";
import { disponibiliteLabel } from "@/v2/lib/disponibiliteLabel";
import { Home, ChevronLeft, ChevronRight, MapPin, Calendar, Bed, Ruler, Mail, RotateCcw, Check } from "lucide-react";
import { useBiens } from "@/hooks/useBiens";
import { DataTable, type ColumnDef } from "@/v2/components/DataTable";
import { isAppartForRent, filterVisibleBiens } from "@/v2/lib/bienLifecycle";
import { useRole } from "@/contexts/RoleContext";
import { useBiensFullReadRole } from "@/hooks/useBiensPermRole";
import { cn } from "@/lib/utils";
import type { BienRow } from "@/schemas/bien";
import { CheckElsBadges } from "@/v2/components/CheckElsBadges";
import { setListOrder } from "@/hooks/useListNavigation";
import { BienDetailModal } from "@/v2/components/BienDetailModal";
// Filtres partagés avec la page Apparts for Rent V1 (parité C-Level / collaborateur).
import { CheckDropdown, Toggle, PostalCodeFilter } from "@/components/apparts/FilterControls";

/**
 * Apartments for Rent — outil métier dédié à la commercialisation locative.
 *
 * Diffère du Catalogue (admin / browsing) :
 * - Filtre auto : bien doit être débloqué CLevel ET non vendu (la location
 *   active OU les loués récents qui peuvent revenir).
 * - Cartes visuelles plutôt qu'un tableau dense.
 * - Champs centrés sur le marketing : loyer, charges, surface, chambres, PEB.
 * - Tri par défaut : disponibilité descendante (le neuf en haut).
 */

const PAGE_SIZE = 24;

const formatDate = (s: string | null | undefined, lng: string) => {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString(localeFromLng(lng), { day: "2-digit", month: "short", year: "numeric" });
};

type ViewMode = "cards" | "table";

const PEB_COLORS: Record<string, string> = {
  A: "bg-success/15 text-success",
  B: "bg-success/15 text-success",
  C: "bg-accent/15 text-accent",
  D: "bg-warning/15 text-warning",
  E: "bg-warning/15 text-warning",
  F: "bg-danger/15 text-danger",
  G: "bg-danger/15 text-danger",
};

type TransactionFilter = "all" | "location" | "vente" | "sous-option";

// Options de filtre alignées sur la V1.
const REGION_OPTIONS = ["bruxelles", "flandre", "wallonie"];
const CHAMBRE_OPTIONS = ["1", "2", "3", "4+"];
const REGION_LABEL_KEY: Record<string, string> = {
  bruxelles: "regions.bruxelles",
  flandre: "regions.flandre",
  wallonie: "regions.wallonie",
};

export function AppartsForRentPage() {
  const { t, i18n } = useTranslation();
  const { role } = useRole();
  // La règle « mailing-ready » (isAppartForRent) a besoin de TOUS les champs
  // mailing : si la RPC en strippe un seul, la complétude échoue et le bien
  // disparaît de la liste.
  //
  // ⚠️ Cette ligne demandait `"c_level"` en dur. Ça a cessé de fonctionner à la
  // migration 099 : `effective_role()` n'accepte plus qu'un rôle de la famille
  // de l'appelant et retombe SILENCIEUSEMENT sur le rôle réel sinon. Un
  // collaborateur recevait donc les colonnes du rôle `collaborateur`, qui ne
  // voit ni `parkingDisponible` ni `garageDisponible` — deux champs exigés sans
  // condition. La page affichait 0 bien pour un collaborateur contre 20 pour un
  // C-Level (constaté en prod le 2026-08-09).
  //
  // On demande maintenant le sous-rôle le plus complet que le serveur
  // accordera réellement (cf. useBiensFullReadRole). Les colonnes AFFICHÉES
  // restent un sous-ensemble marketing ; la fiche ouverte se re-fetch avec le
  // rôle réel.
  const fullReadRole = useBiensFullReadRole();
  const { data: biens, isLoading } = useBiens(fullReadRole);
  // Défaut "location" (demande Thibaud 2026-08-19) : c'est l'écran de travail
  // quotidien, "à louer" est le filtre qu'on veut voir en arrivant.
  const [tx, setTx] = useState<TransactionFilter>("location");
  const [view, setView] = useState<ViewMode>("table");
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  // Filtres de sélection (parité V1) : meublé, company, région, chambres, codes postaux.
  const [meuble, setMeuble] = useState(false);
  const [company, setCompany] = useState(false);
  const [regionFilter, setRegionFilter] = useState<string[]>(REGION_OPTIONS);
  const [chambreFilter, setChambreFilter] = useState<string[]>(CHAMBRE_OPTIONS);
  const [postalCodes, setPostalCodes] = useState<string[]>([]);
  // Modale fiche bien — pour ne pas perdre la liste/filtre/page en cours,
  // on ouvre la fiche en surimpression au lieu de naviguer vers /v2/biens/:id.
  const [modalBienId, setModalBienId] = useState<string | null>(null);

  // Garde-fou métier strict (aligné V1) — bien actif + CLevel OK + mailing
  // complet. La page sert au listing marketing : on ne montre QUE des biens
  // 100 % prêts à publier sur les portails.
  const eligible = useMemo(() => {
    const list = biens || [];
    const { visible } = filterVisibleBiens(list, role);
    return visible.filter(isAppartForRent);
  }, [biens, role]);

  // "Tous" = total réel (sous option comprise). "Location"/"Vente" et "Sous
  // option" se PARTITIONNENT ensuite Tous — chaque bien compte dans exactement
  // une des deux : soit son type de transaction normal, soit "Sous option" s'il
  // est en négociation (jamais les deux, sinon Tous = 2 mais Location + Sous
  // option = 3, incohérent — demande Thibaud 2026-08-19 : "tous 2 - location 1
  // - sous option 1").
  const counts = useMemo(() => {
    const c = { all: eligible.length, location: 0, vente: 0, sousOption: 0 };
    for (const b of eligible) {
      if (b.sousOption) { c.sousOption++; continue; }
      if (b.transaction === "location") c.location++;
      else if (b.transaction === "vente") c.vente++;
    }
    return c;
  }, [eligible]);

  const filtered = useMemo(() => {
    let rows = eligible;
    if (tx === "sous-option") {
      rows = rows.filter((b) => !!b.sousOption);
    } else if (tx !== "all") {
      rows = rows.filter((b) => b.transaction === tx && !b.sousOption);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((b) =>
        [b.referenceInterne, b.reference, b.id, b.adresse, b.ville, b.projet]
          .filter(Boolean).join(" ").toLowerCase().includes(q),
      );
    }
    // Filtres de sélection (mêmes prédicats que la V1).
    const regionSet = new Set(regionFilter);
    const chambreSet = new Set(chambreFilter);
    rows = rows.filter((b) => {
      const matchMeuble = !meuble || !!b.meuble;
      const matchCompany = !company || !!b.companyContract;
      const matchRegion = regionSet.size === 0 || regionSet.has((b.region || "").toLowerCase());
      const nbc = Number(b.nbChambres) || 0;
      const matchChambre = chambreSet.size === 0 || chambreSet.has(nbc >= 4 ? "4+" : String(nbc));
      const matchPostal = postalCodes.length === 0 || postalCodes.includes(b.codePostal || "");
      return matchMeuble && matchCompany && matchRegion && matchChambre && matchPostal;
    });
    return rows;
  }, [eligible, tx, search, meuble, company, regionFilter, chambreFilter, postalCodes]);

  // Reset page quand on change de filtre / search
  useEffect(() => setPage(0), [tx, search, meuble, company, regionFilter, chambreFilter, postalCodes]);

  const resetFilters = () => {
    setTx("all");
    setSearch("");
    setMeuble(false);
    setCompany(false);
    setRegionFilter(REGION_OPTIONS);
    setChambreFilter(CHAMBRE_OPTIONS);
    setPostalCodes([]);
  };

  // Génère le mailing PDF : stocke les IDs filtrés (toute la sélection, pas la
  // page courante) puis ouvre la page /mailing V1 (layout "Apartments For Rent"
  // groupé par projet, A3 paysage, export via window.print()).
  const handleMailing = () => {
    localStorage.setItem("mailing-bien-ids", JSON.stringify(filtered.map((b) => b.id)));
    window.open("/mailing", "_blank");
  };

  // Persiste l'ordre filtré pour la navigation prev/next sur la fiche bien
  // (V1 BienDetailPage lit "biens" en clé par défaut → on aligne dessus).
  useEffect(() => {
    setListOrder("biens", filtered.map((b) => b.id));
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Columns pour vue tableau
  const columns: ColumnDef<BienRow>[] = [
    {
      key: "ref",
      label: t("v2.apparts.col.ref"),
      sortValue: (b) => b.referenceInterne || b.reference,
      render: (b) => <span className="font-mono text-xs">{b.referenceInterne || b.reference || "—"}</span>,
      className: "w-28",
    },
    {
      key: "projet",
      label: t("biens.projet"),
      sortValue: (b) => b.projet,
      render: (b) => <span className="whitespace-nowrap">{b.projet || "—"}</span>,
      className: "w-44",
    },
    {
      key: "adresse",
      label: t("common.address"),
      sortValue: (b) => b.adresse,
      render: (b) => (
        <div>
          <div className="text-text-primary">{b.adresse || "—"}</div>
          <div className="text-xs text-text-muted">
            {[b.codePostal, b.ville].filter(Boolean).join(" ")}
          </div>
        </div>
      ),
    },
    {
      key: "sousOption",
      label: t("v2.apparts.col.sousOption"),
      align: "center",
      sortValue: (b) => (b.sousOption ? 1 : 0),
      render: (b) => b.sousOption ? <Check size={14} className="text-accent mx-auto" /> : <span className="text-text-muted">—</span>,
      className: "w-16",
    },
    {
      key: "chambres",
      label: t("v2.apparts.col.chambresSdb"),
      sortValue: (b) => parseFloat(b.nbChambres || "0") || 0,
      render: (b) =>
        b.nbChambres
          ? <span className="text-sm">{t("v2.apparts.chambresSdb", { ch: b.nbChambres, sdb: b.nbSdb || "—" })}</span>
          : <span className="text-text-muted">—</span>,
      className: "w-28",
    },
    {
      key: "surface",
      label: t("v2.apparts.col.surfaceShort"),
      align: "right",
      sortValue: (b) => parseFloat(b.surfaceHabitable || "0") || 0,
      render: (b) => (b.surfaceHabitable ? `${b.surfaceHabitable} m²` : "—"),
      className: "w-20",
    },
    {
      key: "loyer",
      label: t("v2.apparts.col.loyer"),
      align: "right",
      sortValue: (b) => parseFloat(b.loyerMensuel || "0") || 0,
      render: (b) => (b.loyerMensuel ? <span className="font-semibold">{b.loyerMensuel} €</span> : "—"),
      className: "w-24",
    },
    {
      key: "peb",
      label: t("biens.columns.classePeb"),
      align: "center",
      sortValue: (b) => b.classePeb,
      render: (b) =>
        b.classePeb ? (
          <span className={cn("inline-block px-2 py-0.5 rounded text-xs font-bold", PEB_COLORS[b.classePeb] || "bg-gray-100")}>
            {b.classePeb}
          </span>
        ) : <span className="text-danger">—</span>,
      className: "w-16",
    },
    {
      key: "check",
      label: t("suivi.state"),
      render: (b) => <CheckElsBadges bien={b} size="xs" />,
      className: "w-44",
    },
    {
      key: "libre",
      label: t("v2.apparts.col.libreDes"),
      sortValue: (b) => (b.seraDispoPour ? new Date(b.seraDispoPour) : null),
      render: (b) => formatDate(b.seraDispoPour, i18n.language) ?? "—",
      className: "w-32",
    },
  ];

  const startIdx = safePage * PAGE_SIZE + 1;
  const endIdx = Math.min(startIdx + rows.length - 1, filtered.length);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <Home size={22} className="text-accent" />
          {t("nav.appartsForRent")}
        </h1>
        <p className="text-sm text-text-muted mt-1">
          {t("v2.apparts.subtitle")}
        </p>
      </div>

      {/* Filtres par transaction + view toggle */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {([
          { key: "all", labelKey: "common.all", count: counts.all },
          { key: "location", labelKey: "v2.apparts.chipLocation", count: counts.location },
          { key: "sous-option", labelKey: "v2.catalogue.chips.sousOption", count: counts.sousOption },
        ] as { key: TransactionFilter; labelKey: string; count: number }[]).map((chip) => (
          <button
            key={chip.key}
            type="button"
            onClick={() => setTx(chip.key)}
            className={cn(
              "px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-colors border",
              tx === chip.key
                ? "bg-accent-light text-accent border-accent"
                : "bg-surface text-text-secondary border-border hover:border-accent/30",
            )}
          >
            {t(chip.labelKey)} <span className="text-text-muted ml-1">({chip.count})</span>
          </button>
        ))}

        <div className="ml-auto flex items-center gap-1 border border-border rounded-md p-0.5">
          <button
            type="button"
            onClick={() => setView("cards")}
            className={cn(
              "px-2.5 py-1 text-xs rounded cursor-pointer",
              view === "cards" ? "bg-accent-light text-accent font-medium" : "text-text-muted",
            )}
          >
            {t("v2.apparts.viewCards")}
          </button>
          <button
            type="button"
            onClick={() => setView("table")}
            className={cn(
              "px-2.5 py-1 text-xs rounded cursor-pointer",
              view === "table" ? "bg-accent-light text-accent font-medium" : "text-text-muted",
            )}
          >
            {t("v2.apparts.viewTable")}
          </button>
        </div>
      </div>

      {/* Filtres de sélection (parité V1) */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <CheckDropdown
          label={t("v2.catalogue.chambresLabel")}
          options={CHAMBRE_OPTIONS.map((v) => ({ value: v, label: t("v2.catalogue.chambreOption", { n: v }) }))}
          selected={new Set(chambreFilter)}
          onChange={(s) => setChambreFilter(Array.from(s))}
        />
        <CheckDropdown
          label={t("common.region")}
          options={REGION_OPTIONS.map((v) => ({ value: v, label: t(REGION_LABEL_KEY[v]) }))}
          selected={new Set(regionFilter)}
          onChange={(s) => setRegionFilter(Array.from(s))}
        />
        <Toggle label={t("biens.meuble")} checked={meuble} onChange={setMeuble} />
        <Toggle label={t("v2.catalogue.companyLabel")} checked={company} onChange={setCompany} />
        <PostalCodeFilter codes={postalCodes} onChange={setPostalCodes} />
        <button
          type="button"
          onClick={resetFilters}
          className="flex items-center gap-1.5 px-2.5 py-2 text-xs text-text-muted hover:text-accent cursor-pointer transition-colors"
          title={t("v2.catalogue.resetTitle")}
        >
          <RotateCcw size={13} />
          <span className="hidden sm:inline">{t("common.resetFilters")}</span>
        </button>
      </div>

      {/* Recherche + compteur + génération mailing */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("v2.apparts.searchPlaceholder")}
            className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-surface outline-none"
          />
        </div>
        <span className="text-sm text-text-muted whitespace-nowrap">{t("v2.apparts.aptCount", { count: filtered.length })}</span>
        <button
          type="button"
          onClick={handleMailing}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text-secondary border border-dashed border-border rounded-md hover:border-accent hover:text-accent bg-surface transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          title={t("v2.apparts.generateMailingTitle")}
        >
          <Mail size={14} />
          <span>{t("v2.apparts.generateMailing")}</span>
        </button>
      </div>

      {isLoading ? (
        <div className="text-center text-text-muted py-12">{t("common.loading")}</div>
      ) : view === "cards" ? (
        rows.length === 0 ? (
          <div className="text-center text-text-muted bg-surface border border-border rounded-md py-12">
            {t("v2.catalogue.emptyFilter")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((b) => <BienCard key={b.id} bien={b} onClick={() => setModalBienId(b.id)} />)}
          </div>
        )
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(b) => b.id}
          onRowClick={(b) => setModalBienId(b.id)}
          searchable={false}
          emptyLabel={t("v2.apparts.emptyTable")}
        />
      )}

      <BienDetailModal bienId={modalBienId} onClose={() => setModalBienId(null)} preset="apparts_for_rent" />

      {filtered.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-text-muted">
            {t("v2.catalogue.paginationRange", { start: startIdx, end: endIdx, total: filtered.length })}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="p-1.5 rounded-md border border-border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs px-3 py-1.5 bg-surface border border-border rounded-md font-medium">
              {t("v2.catalogue.pageOf", { page: safePage + 1, total: pageCount })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              className="p-1.5 rounded-md border border-border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BienCard({ bien: b, onClick }: { bien: BienRow; onClick: () => void }) {
  const { t, i18n } = useTranslation();
  const dispo = (b.disponibilite || "").toUpperCase();
  // Photo de couverture = 1re photo de la galerie (l'ordre est curé à la main).
  const coverUrl = b.photos && b.photos.length > 0 ? b.photos[0]?.url : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-surface border border-border rounded-lg overflow-hidden hover:border-accent transition-colors cursor-pointer group"
    >
      <div className="aspect-[16/10] bg-gradient-to-br from-accent/10 to-accent/5 flex items-center justify-center relative overflow-hidden">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={b.adresse || ""}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <Home size={32} className="text-accent/40" />
        )}
        {dispo && (
          <span
            className={cn(
              "absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded",
              dispo === "DISPONIBLE" && "bg-success text-white",
              dispo === "LOUÉ" && "bg-accent text-white",
            )}
          >
            {disponibiliteLabel(b.disponibilite, t)}
          </span>
        )}
        {b.classePeb && (
          <span
            className={cn(
              "absolute top-2 left-2 px-1.5 py-0.5 rounded text-xs font-bold",
              PEB_COLORS[b.classePeb] || "bg-gray-100",
            )}
          >
            {t("v2.apparts.pebLabel", { class: b.classePeb })}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1 text-xs">
          <span className="font-mono text-text-muted">{b.referenceInterne || b.reference || "—"}</span>
          {b.projet && <span className="text-text-muted">· {b.projet}</span>}
        </div>
        <div className="text-sm font-semibold text-text-primary truncate group-hover:text-accent transition-colors">
          {b.adresse || "—"}
        </div>
        <div className="flex items-center gap-1 mt-0.5 text-xs text-text-muted">
          <MapPin size={11} />
          {[b.codePostal, b.ville].filter(Boolean).join(" ")}
        </div>
        <div className="flex items-center gap-3 mt-2 text-xs text-text-secondary">
          {b.surfaceHabitable && (
            <span className="inline-flex items-center gap-1"><Ruler size={11} /> {b.surfaceHabitable} m²</span>
          )}
          {b.nbChambres && (
            <span className="inline-flex items-center gap-1"><Bed size={11} /> {t("v2.apparts.chambresShort", { n: b.nbChambres })}</span>
          )}
          {b.seraDispoPour && (
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {formatDate(b.seraDispoPour, i18n.language)}</span>
          )}
        </div>
        {b.loyerMensuel && (
          <div className="mt-2 pt-2 border-t border-border/50 flex items-baseline gap-1">
            <span className="text-lg font-bold text-text-primary">{b.loyerMensuel}</span>
            <span className="text-xs text-text-muted">{t("v2.apparts.perMonth")}</span>
            {b.chargesMensuelles && (
              <span className="text-xs text-text-muted ml-1">{t("v2.apparts.chargesSuffix", { amount: b.chargesMensuelles })}</span>
            )}
          </div>
        )}
        {/* Check ELS — informe immédiatement sur l'état dossier */}
        <div className="mt-2">
          <CheckElsBadges bien={b} size="xs" showLabels />
        </div>
      </div>
    </button>
  );
}
